/**
 * Comparative growth chart for the YieldSimulatorWidget.
 *
 * Renders all fixed-income products as overlaid linear-accrual lines
 * sharing the same `amount` baseline. The currently-selected product
 * is drawn solid + thick; the others sit behind as dashed ghosts so
 * the eye can compare without losing focus on the selection.
 *
 * Marketing convention: accrual is shown as a smooth daily line even
 * though fixed-income notes only PAY at maturity. The canonical risk
 * disclaimer at the foot of the host widget covers these projected
 * lines (audit #140) — repeating it here would over-clutter.
 *
 * X-axis is days from today, to the end the person chose (`horizonDays`, whole terms of the
 * selected Series, up to about five years) or at least the longest term shown. Each Series
 * starts its next term by itself, so its line carries each completed term's result forward.
 * Y-axis baseline is the principal amount (not zero) — the user
 * cares about the GAIN, and a zero baseline would flatten the
 * differences between products with similar yields.
 */
import { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatMoney } from '../lib/datetime';
import { colors } from '../styles/theme';
import type { ProductSummary } from '../services/portfolio.service';
import { t } from '../i18n/language';

/** The product the client picked is the brand's gold line; the others are quiet navy
 *  dashes to compare against. A growth chart never draws a product in a warning colour. */
const SELECTED_LINE_COLOR = colors.gold;
const OTHER_LINE_COLOR = colors.navy;

interface Props {
  readonly products: ReadonlyArray<ProductSummary>;
  readonly selectedId: string | null;
  readonly amount: number;
  readonly currency: string;
  /** The end the person chose, in days from today; 0 = one term of each Series. */
  readonly horizonDays?: number;
}

interface SeriesPoint {
  day: number;
  /** Maps product.id → projected value on that day. recharts pulls
   *  each Line's `dataKey` from this dict. */
  [productKey: string]: number;
}

/** Value on `day` of a Series whose result joins the capital at the end of each term: whole
 *  terms compounded, linear accrual inside the current one. Pure. */
export function projectedValue(amount: number, rate: number, termDays: number, day: number): number {
  const done = Math.floor(day / termDays);
  const inTerm = day - done * termDays;
  return amount * (1 + (rate * termDays) / 365) ** done * (1 + (rate * inTerm) / 365);
}

/** One row per `day` in [0, end], stepping wide enough to keep the recharts payload small
 *  (no need for daily resolution on a marketing chart). Pure. */
export function buildSeries(
  products: ReadonlyArray<ProductSummary>,
  amount: number,
  horizonDays = 0,
): SeriesPoint[] {
  const usable = products.filter(
    (p) => p.terms_available && p.target_yield && p.term_days && Number.parseFloat(p.target_yield) > 0,
  );
  if (usable.length === 0 || amount <= 0) return [];

  const maxTerm = Math.max(horizonDays, ...usable.map((p) => p.term_days as number));
  // ~30 segments across the full range — smooth enough to read as a
  // curve, sparse enough that the recharts SVG stays light.
  const step = Math.max(1, Math.ceil(maxTerm / 30));

  const days: number[] = [];
  for (let d = 0; d <= maxTerm; d += step) days.push(d);
  // Always include the exact end so the rightmost tick lines up.
  if (days[days.length - 1] !== maxTerm) days.push(maxTerm);

  return days.map((day) => {
    const point: SeriesPoint = { day };
    for (const p of usable) {
      const rate = Number.parseFloat(p.target_yield as string);
      point[p.id] = projectedValue(amount, rate, p.term_days as number, day);
    }
    return point;
  });
}

export default function SimulatorProjection({
  products,
  selectedId,
  amount,
  currency,
  horizonDays = 0,
}: Props): JSX.Element | null {
  const series = useMemo(
    () => buildSeries(products, amount, horizonDays),
    [products, amount, horizonDays],
  );
  const usable = useMemo(
    () => products.filter((p) => p.terms_available && p.target_yield && p.term_days),
    [products],
  );

  if (series.length === 0 || usable.length === 0) return null;

  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 8,
          textAlign: 'center',
        }}
      >
        Growth — all series compared
      </div>
      {/* The chart takes the width it is given: its proportions are never tuned
          to make an estimate look steeper than it is. */}
      <div>
        <ResponsiveContainer width="100%" height={240}>
        <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid stroke={colors.border} strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tick={{ fontSize: 11, fill: colors.textMuted }}
            tickFormatter={(d: number) =>
              d > 400 ? `${(d / 365).toFixed(1)}${t('y', 'years')}` : `${d}${t('d', 'days')}`
            }
          />
          <YAxis
            tick={{ fontSize: 11, fill: colors.textMuted }}
            tickFormatter={(v: number) =>
              v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)
            }
            domain={['auto', 'auto']}
          />
          <Tooltip
            formatter={(value: number) => formatMoney(value, currency)}
            labelFormatter={(d: number) => t(`Day ${d}`)}
            contentStyle={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {usable.map((p) => {
            const isSelected = p.id === selectedId;
            const stroke = isSelected ? SELECTED_LINE_COLOR : OTHER_LINE_COLOR;
            return (
              <Line
                key={p.id}
                type="monotone"
                dataKey={p.id}
                name={p.name}
                stroke={stroke}
                strokeWidth={isSelected ? 3 : 1.5}
                strokeDasharray={isSelected ? undefined : '4 4'}
                strokeOpacity={isSelected ? 1 : 0.55}
                dot={false}
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
